/**
 * Admin console UI kit — the small set of primitives every page shares so the
 * console reads as one tool: page header, cards, badges, buttons, dense tables
 * with a sticky header, state rows (loading / empty / error), pagination,
 * search with a "/" shortcut, and copy-to-clipboard ids.
 *
 * Dark-only by design (see globals.css) — the operator never confuses this
 * with the customer app.
 */
import { Check, Copy, Search, X } from 'lucide-react';
import {
  type ButtonHTMLAttributes,
  type ReactNode,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';

import { cn } from '../lib/cn';
import { errorMessage, useSlashFocus } from '../lib/ui-utils';


// ── layout ──────────────────────────────────────────────────────────────────

export function PageHeader(props: { title: ReactNode; description?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <h1 className="truncate text-xl font-semibold text-neutral-100">{props.title}</h1>
        {props.description ? <p className="mt-0.5 text-sm text-neutral-400">{props.description}</p> : null}
      </div>
      {props.actions ? <div className="flex flex-wrap gap-2">{props.actions}</div> : null}
    </header>
  );
}

export function Card(props: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn('rounded-lg border border-neutral-800 bg-neutral-900 p-4 sm:p-5', props.className)}>
      {props.title || props.actions ? (
        <div className="mb-3 flex items-center justify-between gap-2">
          {props.title ? <h2 className="text-sm font-medium text-neutral-300">{props.title}</h2> : <span />}
          {props.actions}
        </div>
      ) : null}
      {props.children}
    </section>
  );
}

export function DataRow({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-0.5 text-sm">
      <dt className="shrink-0 text-neutral-500">{label}</dt>
      <dd className="min-w-0 text-right text-neutral-200">{value}</dd>
    </div>
  );
}

// ── badges + buttons ────────────────────────────────────────────────────────

export type Tone = 'ok' | 'warn' | 'danger' | 'brand' | 'neutral' | 'info';

const BADGE_TONES: Record<Tone, string> = {
  ok: 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/30',
  warn: 'bg-amber-500/15 text-amber-300 ring-amber-500/30',
  danger: 'bg-red-500/15 text-red-300 ring-red-500/30',
  brand: 'bg-orange-500/15 text-orange-300 ring-orange-500/30',
  neutral: 'bg-neutral-700/30 text-neutral-400 ring-neutral-700',
  info: 'bg-sky-500/15 text-sky-300 ring-sky-500/30',
};

export function Badge(props: { tone: Tone; children: ReactNode; title?: string }) {
  return (
    <span
      title={props.title}
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ring-1',
        BADGE_TONES[props.tone],
      )}
    >
      {props.children}
    </span>
  );
}

const BUTTON_TONES = {
  default: 'bg-neutral-800 text-neutral-200 ring-neutral-700 hover:bg-neutral-700',
  primary: 'bg-orange-600 text-white ring-orange-500/40 hover:bg-orange-700',
  danger: 'bg-red-700 text-white ring-red-600/40 hover:bg-red-800',
  ok: 'bg-emerald-700 text-white ring-emerald-600/40 hover:bg-emerald-800',
  ghost: 'bg-transparent text-neutral-300 ring-transparent hover:bg-neutral-800',
} as const;

export function Button(
  props: ButtonHTMLAttributes<HTMLButtonElement> & { tone?: keyof typeof BUTTON_TONES; icon?: ReactNode },
) {
  const { tone = 'default', icon, className, children, type = 'button', ...rest } = props;
  return (
    <button
      type={type}
      {...rest}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ring-1 transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500',
        'disabled:cursor-not-allowed disabled:opacity-40',
        BUTTON_TONES[tone],
        className,
      )}
    >
      {icon}
      {children}
    </button>
  );
}

// ── copy-to-clipboard ───────────────────────────────────────────────────────

/** Monospace id with a one-click copy.  `short` shows the first 8 chars. */
export function CopyId(props: { value: string; short?: boolean; label?: string; className?: string }) {
  const [done, setDone] = useState(false);
  const shown = props.short ? `${props.value.slice(0, 8)}…` : props.value;
  return (
    <button
      type="button"
      title={`Copy ${props.label ?? 'id'}: ${props.value}`}
      aria-label={`Copy ${props.label ?? 'id'}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void navigator.clipboard
          ?.writeText(props.value)
          .then(() => {
            setDone(true);
            setTimeout(() => setDone(false), 1200);
          })
          .catch(() => toast.error('Clipboard unavailable'));
      }}
      className={cn(
        'group inline-flex max-w-full items-center gap-1 rounded px-1 font-mono text-xs text-neutral-400 hover:bg-neutral-800 hover:text-neutral-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500',
        props.className,
      )}
    >
      <span className="truncate">{shown}</span>
      {done ? (
        <Check size={11} className="shrink-0 text-emerald-400" />
      ) : (
        <Copy size={11} className="shrink-0 opacity-50 group-hover:opacity-100" />
      )}
    </button>
  );
}

// ── states ──────────────────────────────────────────────────────────────────


export function ErrorBanner(props: { error: unknown; onRetry?: () => void }) {
  return (
    <div
      role="alert"
      className="mb-4 flex items-center justify-between gap-3 rounded-md border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-300"
    >
      <span className="min-w-0 break-words">{errorMessage(props.error)}</span>
      {props.onRetry ? (
        <button onClick={props.onRetry} className="shrink-0 rounded px-2 py-1 text-xs ring-1 ring-red-500/40 hover:bg-red-500/10">
          Retry
        </button>
      ) : null}
    </div>
  );
}

/** Block-level loading / empty placeholder for cards. */
export function Muted({ children }: { children: ReactNode }) {
  return <p className="text-sm text-neutral-500">{children}</p>;
}

// ── tables ──────────────────────────────────────────────────────────────────

/**
 * Scroll container for dense tables: horizontal scroll on narrow screens and
 * a bounded height on desktop so `<thead className={THEAD}>` stays sticky.
 */
export function TableShell({ children }: { children: ReactNode }) {
  return (
    <div className="max-h-[calc(100vh-15rem)] overflow-auto rounded-lg border border-neutral-800 bg-neutral-900 max-sm:max-h-none">
      <table className="w-full text-sm sm:min-w-[640px]">{children}</table>
    </div>
  );
}

export const THEAD =
  'sticky top-0 z-10 bg-neutral-900 text-left text-xs uppercase tracking-wider text-neutral-500 shadow-[inset_0_-1px_0_var(--color-neutral-800)]';
export const TH = 'px-3 py-2 font-medium';
export const TD = 'px-3 py-2 align-top';
export const TR = 'hover:bg-neutral-800/40';
/** Secondary column — hidden on phones so the table fits 375px without sideways scroll. */
export const WIDE = 'hidden sm:table-cell';

export function TableStateRow(props: {
  colSpan: number;
  loading: boolean;
  error?: unknown;
  empty: boolean;
  emptyText: ReactNode;
}) {
  let content: ReactNode = null;
  if (props.loading) content = 'Loading…';
  else if (props.error) content = <span className="text-red-300">{errorMessage(props.error)}</span>;
  else if (props.empty) content = props.emptyText;
  if (content === null) return null;
  return (
    <tr>
      <td colSpan={props.colSpan} className="px-4 py-10 text-center text-neutral-500">
        {content}
      </td>
    </tr>
  );
}

export function Pagination(props: { total: number; offset: number; pageSize: number; onChange: (offset: number) => void }) {
  if (props.total <= props.pageSize) return null;
  return (
    <div className="mt-3 flex items-center justify-between text-sm text-neutral-400">
      <div>
        {props.offset + 1}–{Math.min(props.offset + props.pageSize, props.total)} of {props.total.toLocaleString()}
      </div>
      <div className="flex items-center gap-2">
        <Button disabled={props.offset === 0} onClick={() => props.onChange(Math.max(0, props.offset - props.pageSize))}>
          ← Prev
        </Button>
        <Button
          disabled={props.offset + props.pageSize >= props.total}
          onClick={() => props.onChange(props.offset + props.pageSize)}
        >
          Next →
        </Button>
      </div>
    </div>
  );
}

// ── filters ─────────────────────────────────────────────────────────────────


export function SearchInput(props: { value: string; onChange: (v: string) => void; placeholder: string }) {
  const ref = useRef<HTMLInputElement>(null);
  useSlashFocus(ref);
  return (
    <div className="relative min-w-0 basis-full sm:max-w-md sm:flex-1 sm:basis-auto">
      <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
      <input
        ref={ref}
        type="text"
        inputMode="search"
        enterKeyHint="search"
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            props.onChange('');
            e.currentTarget.blur();
          }
        }}
        placeholder={props.placeholder}
        aria-label={props.placeholder}
        className="w-full rounded-md bg-neutral-900 py-2 pl-9 pr-14 text-base text-neutral-100 outline-none ring-1 ring-neutral-800 placeholder:text-neutral-600 focus:ring-2 focus:ring-orange-500 sm:text-sm"
      />
      {props.value ? (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => props.onChange('')}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-neutral-500 hover:bg-neutral-800 hover:text-neutral-300"
        >
          <X size={14} />
        </button>
      ) : (
        <kbd className="pointer-events-none absolute right-2 top-1/2 hidden -translate-y-1/2 rounded border border-neutral-700 px-1.5 text-[10px] text-neutral-500 sm:block">
          /
        </kbd>
      )}
    </div>
  );
}

export function SelectFilter(props: {
  label: string;
  value: string | undefined;
  options: readonly string[];
  onChange: (v: string | undefined) => void;
  allLabel?: string;
}) {
  return (
    <label className="flex items-center gap-2 rounded-md bg-neutral-900 px-2 py-1.5 text-xs ring-1 ring-neutral-800 focus-within:ring-2 focus-within:ring-orange-500">
      <span className="text-neutral-500">{props.label}</span>
      <select
        value={props.value ?? ''}
        onChange={(e) => props.onChange(e.target.value || undefined)}
        className="bg-transparent text-neutral-200 outline-none"
      >
        <option value="">{props.allLabel ?? 'all'}</option>
        {props.options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

export function FilterChip(props: { label: ReactNode; active: boolean; onClick: () => void; icon?: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={props.active}
      onClick={props.onClick}
      className={cn(
        'flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ring-1 transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500',
        props.active
          ? 'bg-orange-500/20 text-orange-300 ring-orange-500/50'
          : 'bg-neutral-900 text-neutral-400 ring-neutral-800 hover:text-neutral-200',
      )}
    >
      {props.icon}
      {props.label}
    </button>
  );
}
